import type { Transition } from 'motion/react';
import {
  EXTEND_BATCH_MS,
  OVERSHOOT_FRACTION,
  REBOUND_EASE,
  REBOUND_T,
  SKIP_DECEL_MS,
  SLOT_CURVE,
  type AnimMode,
} from './readyStep.constants';

export interface SlotSpinAnimation {
  laneAnimate: { x: number | number[] };
  laneTransition: Transition;
  blurAnimate: { filter: string | string[] };
  blurTransition: Transition;
}

/**
 * Pure derivation of motion targets + transitions for the slot lane.
 * Pulled out of `ReadyStep` so the component body stays focused on
 * state-machine wiring and JSX.
 *
 * Geometry:
 *   • The lane overshoots by `OVERSHOOT_FRACTION` of a tile, then
 *     snaps back to `finalX` — the same slot-machine "rebound" feel
 *     real reels have. A single smooth `SLOT_CURVE` drives the whole
 *     travel (no accel/cruise/decel segments — those read as a slow
 *     start that "suddenly speeds up"); readability of the landing
 *     comes from the spin DURATION, not from segmenting the curve.
 *   • `skip` decelerates straight from wherever the lane is to the
 *     active landing X over `SKIP_DECEL_MS` — the player's fast-forward.
 *   • `spin_hold` continues scrolling at constant velocity past `finalX`
 *     in EXTEND_STEPS-tile batches while waiting for the backend.
 *   • Outside a live spin the lane rests at the active landing X so
 *     the landed tile stays centred; otherwise it rests at `x=0`.
 *
 * Blur is disabled — kept as a static `filter: none` no-op. Animating it on
 * the inner wrapper (which holds the ENTIRE tile row) re-rasterized an
 * oversized layer every frame and froze weak devices; see the note at the
 * blur assignment below. The translate keeps `onAnimationComplete` as the
 * single source of truth for "the spin finished".
 */
export function slotSpinAnimation({
  animMode,
  spinSteps,
  spinDuration,
  tileStep,
  extendBatch = 0,
  extendSteps = 0,
  baseOffset = 0,
}: {
  animMode: AnimMode;
  spinSteps: number;
  spinDuration: number;
  /** Travel unit per tile. Pass the pixel pitch for a px-driven lane, or `1`
   *  to get the output in TILE UNITS (the consumer then maps to pixels with a
   *  live pitch, so a viewport resize re-centres without re-animating). */
  tileStep: number;
  /** How many extend batches have been committed (0 = no extend yet). */
  extendBatch?: number;
  /** Tiles per extend batch (EXTEND_STEPS). */
  extendSteps?: number;
  /** Px correction so the centre tile sits dead-centre at x=0. Measured off
   *  the live DOM because `justify-center` mis-centres the (huge) tile strip
   *  on iOS — it rests a constant ~15 tiles off, which otherwise lands every
   *  spin on the wrong tile. 0 when centring is already exact (desktop). */
  baseOffset?: number;
}): SlotSpinAnimation {
  const totalDistance = spinSteps * tileStep;
  const finalX = -totalDistance;
  const overshootX = -(totalDistance + tileStep * OVERSHOOT_FRACTION);

  // How many extend batches the lane has actually travelled into. `extendBatch`
  // is the committed count — 1 on the FIRST hold batch (see CardShuffleReel),
  // not 0 — so `landingX` below stays in sync with where the lane physically
  // is. The `spin_hold` fallback covers the single frame between entering the
  // hold and that committed count landing in state. Counting the first batch
  // is what stops `cleared` from teleporting the lane back to `finalX`.
  const heldBatches = Math.max(extendBatch, animMode === 'spin_hold' ? 1 : 0);

  // End of the current extend batch (or finalX when no hold ever happened).
  const extendFinalX =
    heldBatches > 0
      ? -(spinSteps + extendSteps * heldBatches) * tileStep
      : finalX;

  // Where the lane rests after all motion stops — the SAME spot the last batch
  // travelled to, so the rest transition is a no-op rather than a visible jump.
  const landingX = heldBatches > 0 ? extendFinalX : finalX;

  const isSpinning = animMode === 'spin';
  const isSkipping = animMode === 'skip';
  const isExtending = animMode === 'spin_hold';
  const restX =
    isSkipping ||
    isExtending ||
    animMode === 'cleared' ||
    animMode === 'reveal' ||
    animMode === 'aborting' ||
    animMode === 'errored'
      ? landingX
      : 0;

  let laneAnimate: { x: number | number[] };
  let laneTransition: Transition;
  if (isSpinning) {
    laneAnimate = { x: [0, overshootX, finalX] };
    laneTransition = {
      duration: spinDuration / 1000,
      times: [0, 1 - REBOUND_T, 1],
      ease: [SLOT_CURVE, REBOUND_EASE],
    };
  } else if (isExtending) {
    laneAnimate = { x: extendFinalX };
    laneTransition = { duration: EXTEND_BATCH_MS / 1000, ease: 'linear' };
  } else if (isSkipping) {
    laneAnimate = { x: landingX };
    laneTransition = { duration: SKIP_DECEL_MS / 1000, ease: 'easeOut' };
  } else {
    laneAnimate = { x: restX };
    laneTransition = { duration: 0 };
  }

  // Shift every keyframe by the measured centring correction so the target
  // tile lands dead-centre even when justify-center is off (iOS).
  if (baseOffset !== 0) {
    laneAnimate.x = Array.isArray(laneAnimate.x)
      ? laneAnimate.x.map((v) => v + baseOffset)
      : laneAnimate.x + baseOffset;
  }

  // Motion blur intentionally disabled. Animating `filter: blur()` here put
  // the filter on the inner wrapper that holds the ENTIRE tile row — a lane
  // tens of thousands of px wide, far beyond the GPU max texture size. The
  // browser re-rasterized that whole oversized layer every frame and fell
  // back to CPU raster (multi-hundred-ms frames), which froze weak devices.
  // The 2px softening was barely perceptible and the slot feel comes from the
  // velocity curve + overshoot, not the blur. Kept as a static no-op so the
  // consumers' inner wrapper needs no structural change.
  const blurAnimate = { filter: 'none' };
  const blurTransition: Transition = { duration: 0 };

  return { laneAnimate, laneTransition, blurAnimate, blurTransition };
}
